/*
 * Môdern Studio™ Projects + Homes (AI+ feature)
 * ------------------------------------------------------------
 * Browser-based (localStorage), same honesty-scoped pattern as wishlist.js.
 *
 * Projects link a Mood Board + Space Plan save for one room. Homes are the
 * layer above that: a named group (e.g. "My Apartment") referencing a set
 * of Project IDs, so a customer with several rooms in progress — living
 * room, bedroom, office — can see them together as one home instead of a
 * flat, disconnected list. A Home stores REFERENCES (project IDs), not
 * copies, since Projects are the source of truth and can keep changing
 * (mood board/space plan attached later) without the Home needing updating.
 *
 * Phase 4D.3: storage is scoped per Shopify customer ID. Each caller
 * (Mood Board, Space Planner, My Projects) must call setCustomer(customerId)
 * with the signed-in customer's ID before any read/write, so that two
 * different customers sharing one browser never read or write each other's
 * data. Callers already read this ID for the existing signed-token flow —
 * this reuses that same value, no new identity mechanism.
 *
 * If no customer is set, every read returns an empty result and every
 * write is a no-op. There is no anonymous namespace, and unscoped calls
 * never fall back to the old global keys.
 *
 * The pre-4D.3 global keys (modernStudioProjects / modernStudioHomes) are
 * left untouched here -- they are never read or written by the normal
 * read/write/create/attach/delete functions any more. They are
 * only ever read by hasLegacyData()/importLegacyData(), which exist solely
 * to power the explicit, customer-confirmed import prompt on My Projects,
 * and (Phase 6B) by the read-only readLocalForMigration().
 * Nothing here deletes them; import is a COPY, not a MOVE.
 *
 * Phase 4D.4: writeKey() now returns true/false instead of silently
 * swallowing localStorage failures. Every mutating public function
 * (createProject, createHome, attachMoodBoard, attachSpacePlan,
 * assignProjectToHome, removeProjectFromHome, deleteHome) now returns
 * null/false on persistence failure -- exactly the same falsy result it
 * already returned for a logical failure (e.g. project not found), so no
 * existing caller's success-detection (`if (result) {...}`) needed to
 * change. deleteProject returns true only if the primary projects-list
 * write succeeds; a secondary Home-reference cleanup failure is not
 * escalated to a caller-visible failure, since My Projects' own rendering
 * already tolerates a Home referencing a since-removed project ID (it
 * resolves each ID with getProject() and filters out anything missing) --
 * escalating that secondary write would need a heavier rollback than this
 * case warrants. There is no persistent in-memory cache anywhere in this
 * file (every get* function re-reads localStorage fresh), so a failed
 * write can never leave a caller's next read out of sync with what's
 * actually stored.
 *
 * Phase 5D: controlled read-only cloud snapshot support.
 *
 * When My Projects successfully reads Projects/Homes from the Phase 5D
 * cloud adapter, it can provide those records through setCloudSnapshot().
 * Existing UI callers can continue using MSGProjects.getProjects(),
 * getHomes(), getProject(), etc. without needing a second rendering model.
 *
 * The cloud snapshot is MEMORY ONLY:
 *   - it is never written into localStorage;
 *   - it is cleared whenever setCustomer() is called;
 *   - localStorage remains the fallback whenever no cloud snapshot exists.
 *
 * While a cloud snapshot is active, local Project/Home mutation methods
 * fail closed. This prevents a cloud-backed record from accidentally being
 * copied or mutated into localStorage during the controlled Phase 5D rollout.
 *
 * Phase 5D.3A adds one memory-only bridge for confirmed server writes:
 * upsertCloudProject(project). The network mutation itself still happens
 * through MSGCloudProjects; this file only accepts the normalized Project
 * returned by the server and updates the active in-memory snapshot so the
 * existing synchronous renderers can immediately see it. Nothing is written
 * to localStorage.
 *
 * Phase 5D.4 adds the matching removal bridge: removeCloudProject(projectId).
 * Like upsertCloudProject, it never performs a network call itself -- the
 * delete happens through MSGCloudProjects.deleteProject(), and only after
 * that succeeds does the caller tell this file to drop the Project from the
 * active in-memory snapshot. It touches only cloudProjectsSnapshot; Homes
 * and every other Project are left exactly as they were, and nothing is
 * written to localStorage.
 *
 * Phase 6A-2 adds refreshCloudProject(project) / refreshCloudHome(home):
 * memory-only, in-place replacements used when the server reports a
 * version conflict and returns its current copy of a record. They keep the
 * record's position in the list, return false if the record is not already
 * in the snapshot, and never touch localStorage.
 *
 * Phase 6B #3 (writes-OFF behavior): the account view can be showing while
 * account saving is off (or its status is unknown). Then:
 *   - account records stay visible and openable but are never changed here;
 *   - new work is saved on this device through MSGProjects.device, which
 *     always reads/writes this customer's browser copies, snapshot or not;
 *   - browser copies already in the account (by clientLegacyId) or settled by
 *     the move (marker resolvedIds: 'moved' / 'deleted_in_account') are never
 *     shown as browser-only work, so a moved-then-deleted Project is not
 *     presented as fresh (same rule as the move itself).
 * accountWrites is 'on' only when BOTH list replies said writesEnabled:true;
 * anything else ('off', mixed, missing, unreadable) is treated as off.
 */

(function () {
  var LEGACY_PROJECTS_KEY = 'modernStudioProjects';
  var LEGACY_HOMES_KEY = 'modernStudioHomes';

  var currentCustomerId = null;

  // Phase 5D read-only cloud snapshot.
  // null means "no cloud snapshot is active", which preserves the original
  // customer-scoped localStorage behavior exactly.
  var cloudProjectsSnapshot = null;
  var cloudHomesSnapshot = null;

  // Phase 6B #3: 'on' | 'off' | 'unknown'. Only 'on' allows account writes.
  var accountWrites = 'unknown';

  function setAccountWrites(value) {
    accountWrites = value === 'on' || value === 'off' ? value : 'unknown';
    return accountWrites;
  }
  function getAccountWrites() { return accountWrites; }

  // Where new work is saved right now: the account only when the account view
  // is showing AND account saving is confirmed on; otherwise this device.
  function saveTarget() {
    return hasCloudSnapshot() && accountWrites === 'on' ? 'account' : 'device';
  }

  function clearCloudSnapshot() {
    cloudProjectsSnapshot = null;
    cloudHomesSnapshot = null;
  }

  function setCloudSnapshot(projects, homes, opts) {
    if (!currentCustomerId) return false;
    if (!Array.isArray(projects) || !Array.isArray(homes)) return false;
    if (opts && typeof opts === 'object' && 'writes' in opts) setAccountWrites(opts.writes);

    // Arrays are copied so callers cannot replace/reorder our snapshot
    // through an array reference. Project/Home objects themselves are
    // treated as read-only while this snapshot is active.
    cloudProjectsSnapshot = projects.slice();
    cloudHomesSnapshot = homes.slice();

    return true;
  }

  function hasCloudSnapshot() {
    return cloudProjectsSnapshot !== null &&
      cloudHomesSnapshot !== null;
  }

  // Phase 5D.3A: accept a Project that has already been successfully created
  // or updated by the cloud backend and merge it into the active MEMORY-ONLY
  // snapshot. This does not perform a network write and never touches
  // localStorage. It exists solely so the existing synchronous UI can render
  // the confirmed server record immediately after MSGCloudProjects resolves.
  function upsertCloudProject(project) {
    if (!hasCloudSnapshot()) return false;
    if (!project || typeof project !== 'object' || !project.id) return false;

    var nextProjects = cloudProjectsSnapshot.filter(function (p) {
      return p && p.id !== project.id;
    });

    // New/updated records are placed first, matching the existing local
    // createProject() behavior and the My Projects newest-first presentation.
    nextProjects.unshift(Object.assign({}, project));
    cloudProjectsSnapshot = nextProjects;

    return true;
  }

  // Phase 5D.4: the removal counterpart to upsertCloudProject(). Only
  // removes the Project from the active in-memory cloud snapshot -- it
  // does not call the network, does not touch cloudHomesSnapshot, and does
  // not touch localStorage. Callers are expected to have already confirmed
  // the delete against the cloud backend (MSGCloudProjects.deleteProject())
  // before calling this.
  function removeCloudProject(projectId) {
    if (!hasCloudSnapshot()) return false;
    if (!projectId) return false;

    cloudProjectsSnapshot = cloudProjectsSnapshot.filter(function (p) {
      return p && p.id !== projectId;
    });

    return true;
  }

  // Phase 5D.6: the Home counterpart to upsertCloudProject(). Accepts a
  // Home that has already been successfully created (or updated) by the
  // cloud backend and merges it into the active MEMORY-ONLY snapshot. Does
  // not perform a network write, does not touch cloudProjectsSnapshot, and
  // never touches localStorage.
  function upsertCloudHome(home) {
    if (!hasCloudSnapshot()) return false;
    if (!home || typeof home !== 'object' || !home.id) return false;

    var nextHomes = cloudHomesSnapshot.filter(function (h) {
      return h && h.id !== home.id;
    });

    // New/updated records are placed first, matching upsertCloudProject()
    // and local createHome()'s newest-first presentation.
    nextHomes.unshift(Object.assign({}, home));
    cloudHomesSnapshot = nextHomes;

    return true;
  }

  // Phase 6A-2: replace a cloud record IN PLACE with the newer copy the
  // server returned (for example, the "current" record in a 409 conflict
  // response). Unlike upsert*, this keeps the record's position, so a
  // conflict refresh never reorders My Projects. Returns false (and adds
  // nothing) if the record is not already in the snapshot. Memory only:
  // no network call, never touches localStorage.
  function refreshCloudRecord(list, record) {
    if (!record || typeof record !== 'object' || !record.id) return null;
    var found = false;
    var next = list.map(function (item) {
      if (item && item.id === record.id) {
        found = true;
        return Object.assign({}, record);
      }
      return item;
    });
    return found ? next : null;
  }

  function refreshCloudProject(project) {
    if (!hasCloudSnapshot()) return false;
    var next = refreshCloudRecord(cloudProjectsSnapshot, project);
    if (!next) return false;
    cloudProjectsSnapshot = next;
    return true;
  }

  function refreshCloudHome(home) {
    if (!hasCloudSnapshot()) return false;
    var next = refreshCloudRecord(cloudHomesSnapshot, home);
    if (!next) return false;
    cloudHomesSnapshot = next;
    return true;
  }

  function setCustomer(customerId) {
    currentCustomerId = customerId || null;

    // Never let one customer's in-memory cloud snapshot survive a
    // customer/session change.
    clearCloudSnapshot();
    accountWrites = 'unknown';
  }

  function projectsKey() {
    return currentCustomerId
      ? ('modernStudioProjects:' + currentCustomerId)
      : null;
  }

  function homesKey() {
    return currentCustomerId
      ? ('modernStudioHomes:' + currentCustomerId)
      : null;
  }

  function readKey(key) {
    if (!key) return [];

    try {
      var raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  // Returns true on success, false on any failure (quota exceeded, storage
  // disabled/unavailable, etc.) instead of silently swallowing the error.
  function writeKey(key, list) {
    if (!key) return false;

    try {
      localStorage.setItem(key, JSON.stringify(list));
      return true;
    } catch (e) {
      return false;
    }
  }

  // ---- Phase 6B #3: this customer's browser copies ----
  //
  // localProjects()/localHomes() are the raw stored lists (every mutation
  // reads and writes these, so nothing hidden is ever dropped from storage).
  // The visible lists leave out copies that belong to the account now.
  function localProjects() { return readKey(projectsKey()); }
  function localHomes() { return readKey(homesKey()); }

  function hiddenLocalIds() {
    var hidden = {};
    var resolved = getMigrationMarker().resolvedIds || {};
    Object.keys(resolved).forEach(function (id) {
      if (resolved[id] === 'moved' || resolved[id] === 'deleted_in_account') hidden[id] = true;
    });
    if (hasCloudSnapshot()) {
      cloudProjectsSnapshot.concat(cloudHomesSnapshot).forEach(function (r) {
        if (r && r.clientLegacyId) hidden[r.clientLegacyId] = true;
      });
    }
    return hidden;
  }

  function visibleLocalProjects() {
    var hidden = hiddenLocalIds();
    return localProjects().filter(function (p) {
      return p && typeof p.id === 'string' && !hidden[p.id];
    });
  }

  function visibleLocalHomes() {
    var hidden = hiddenLocalIds();
    return localHomes().filter(function (h) {
      return h && typeof h.id === 'string' && !hidden[h.id];
    }).map(function (h) {
      return Object.assign({}, h, {
        projectIds: (Array.isArray(h.projectIds) ? h.projectIds : []).filter(function (pid) { return !hidden[pid]; })
      });
    });
  }

  // ---- Projects (customer-scoped) ----

  function getProjects() {
    if (hasCloudSnapshot()) {
      return cloudProjectsSnapshot.slice();
    }

    return visibleLocalProjects();
  }

  function saveProjects(projects) {
    return writeKey(projectsKey(), projects);
  }

  function getProjectsForRoom(room) {
    return getProjects().filter(function (p) {
      return p.room === room;
    });
  }

  function getProject(id) {
    var list = getProjects();
    var found = list.filter(function (p) {
      return p.id === id;
    })[0] || null;

    // Phase 6B (D12): in the cloud view, an old browser link such as
    // ?project=proj_<ts>_<n> still opens the Project it was moved into.
    if (!found && id && hasCloudSnapshot()) {
      found = list.filter(function (p) {
        return p.clientLegacyId && p.clientLegacyId === id;
      })[0] || null;
    }

    return found;
  }

  function createProject(name, room, roomLabel) {
    // Phase 5D read-only cloud mode: never mutate/copy cloud records into
    // the local customer-scoped store.
    if (hasCloudSnapshot()) return null;
    return localCreateProject(name, room, roomLabel);
  }

  function localCreateProject(name, room, roomLabel, extra) {
    if (!currentCustomerId) return null;

    var projects = localProjects();

    var project = {
      id: 'proj_' + Date.now() + '_' + Math.floor(Math.random() * 10000),
      name: name,
      room: room,
      roomLabel: roomLabel,
      moodBoard: null,
      spacePlan: null,
      createdAt: Date.now(),
      updatedAt: Date.now()
    };
    if (extra && typeof extra === 'object') Object.assign(project, extra);

    projects.unshift(project);

    if (!saveProjects(projects)) return null;

    return project;
  }

  function attachMoodBoard(projectId, boardData) {
    // Phase 5D read-only cloud mode.
    if (hasCloudSnapshot()) return null;
    return localAttachMoodBoard(projectId, boardData);
  }

  function localAttachMoodBoard(projectId, boardData) {
    if (!currentCustomerId) return null;

    var projects = localProjects();

    var project = projects.filter(function (p) {
      return p.id === projectId;
    })[0];

    if (!project) return null;

    project.moodBoard = boardData;
    project.updatedAt = Date.now();

    if (!saveProjects(projects)) return null;

    return project;
  }

  function attachSpacePlan(projectId, planData) {
    // Phase 5D read-only cloud mode.
    if (hasCloudSnapshot()) return null;
    return localAttachSpacePlan(projectId, planData);
  }

  function localAttachSpacePlan(projectId, planData) {
    if (!currentCustomerId) return null;

    var projects = localProjects();

    var project = projects.filter(function (p) {
      return p.id === projectId;
    })[0];

    if (!project) return null;

    project.spacePlan = planData;
    project.updatedAt = Date.now();

    if (!saveProjects(projects)) return null;

    return project;
  }

  function deleteProject(id) {
    // Phase 5D read-only cloud mode.
    if (hasCloudSnapshot()) return false;
    return localDeleteProject(id);
  }

  function localDeleteProject(id) {
    if (!currentCustomerId) return false;

    var projects = localProjects().filter(function (p) {
      return p.id !== id;
    });

    if (!saveProjects(projects)) return false;

    // Best-effort secondary cleanup: also drop the id from any home that
    // referenced it. If this particular write fails, the primary deletion
    // above already succeeded and is reported as such -- a stray reference
    // left in a home is harmless, since My Projects resolves each
    // reference with getProject() and quietly drops anything missing.
    var homes = localHomes();

    homes.forEach(function (h) {
      h.projectIds = (h.projectIds || []).filter(function (pid) {
        return pid !== id;
      });
    });

    saveHomes(homes);

    return true;
  }

  // ---- Homes (customer-scoped) ----

  function getHomes() {
    if (hasCloudSnapshot()) {
      return cloudHomesSnapshot.slice();
    }

    return visibleLocalHomes();
  }

  function saveHomes(homes) {
    return writeKey(homesKey(), homes);
  }

  function createHome(name) {
    // Phase 5D read-only cloud mode.
    if (hasCloudSnapshot()) return null;
    return localCreateHome(name);
  }

  function localCreateHome(name) {
    if (!currentCustomerId) return null;

    var homes = localHomes();

    var home = {
      id: 'home_' + Date.now() + '_' + Math.floor(Math.random() * 10000),
      name: name,
      projectIds: [],
      createdAt: Date.now(),
      updatedAt: Date.now()
    };

    homes.unshift(home);

    if (!saveHomes(homes)) return null;

    return home;
  }

  function assignProjectToHome(homeId, projectId) {
    // Phase 5D read-only cloud mode.
    if (hasCloudSnapshot()) return null;
    return localAssignProjectToHome(homeId, projectId);
  }

  function localAssignProjectToHome(homeId, projectId) {
    if (!currentCustomerId) return null;

    var homes = localHomes();

    // A project belongs to at most one home at a time -- remove it from any
    // other home first, so it doesn't silently appear twice.
    homes.forEach(function (h) {
      h.projectIds = (h.projectIds || []).filter(function (pid) {
        return pid !== projectId;
      });
    });

    var home = homes.filter(function (h) {
      return h.id === homeId;
    })[0];

    if (!home) return null;

    home.projectIds.push(projectId);
    home.updatedAt = Date.now();

    if (!saveHomes(homes)) return null;

    return home;
  }

  function removeProjectFromHome(projectId) {
    // Phase 5D read-only cloud mode.
    if (hasCloudSnapshot()) return false;
    return localRemoveProjectFromHome(projectId);
  }

  function localRemoveProjectFromHome(projectId) {
    if (!currentCustomerId) return false;

    var homes = localHomes();

    homes.forEach(function (h) {
      h.projectIds = (h.projectIds || []).filter(function (pid) {
        return pid !== projectId;
      });
    });

    return saveHomes(homes);
  }

  // Phase 5D.5: cloud-aware. A cloud Home carries no projectIds -- the
  // relationship is owned by the Project's own homeId field instead
  // (project.homeId -> home.id), the inverse of the local model below.
  // Membership is derived from the Project, never by fabricating or
  // persisting a projectIds array onto a cloud Home object.
  function getHomeForProject(projectId) {
    if (hasCloudSnapshot()) {
      var project = getProject(projectId);
      var homeId = project && project.homeId;

      if (!homeId) return null;

      return getHomes().filter(function (h) {
        return h && h.id === homeId;
      })[0] || null;
    }

    return getHomes().filter(function (h) {
      return h.projectIds.indexOf(projectId) !== -1;
    })[0] || null;
  }

  // Phase 6C: rename a Home saved in this browser. `expectedName` is the
  // name the customer started editing from; if the stored Home has a
  // different name now (another tab), nothing is changed. Reads and writes
  // the FULL stored list, so copies hidden because they were moved are kept.
  // Returns { status: 'renamed', home } | { status: 'changed', name }
  //   | { status: 'missing' } | { status: 'write_failed' } | null.
  function localRenameHome(id, name, expectedName) {
    if (!currentCustomerId || !id || typeof name !== 'string') return null;

    var homes = localHomes();
    var home = homes.filter(function (h) {
      return h && h.id === id;
    })[0];

    if (!home || hiddenLocalIds()[id]) return { status: 'missing' };
    if (typeof expectedName === 'string' && home.name !== expectedName) {
      return { status: 'changed', name: home.name };
    }

    home.name = name;
    home.updatedAt = Date.now();

    if (!saveHomes(homes)) return { status: 'write_failed' };

    return { status: 'renamed', home: Object.assign({}, home) };
  }

  function renameHome(id, name, expectedName) {
    // Phase 5D read-only cloud mode: account Homes are renamed through
    // MSGCloudProjects.editHome, never here.
    if (hasCloudSnapshot()) return null;
    return localRenameHome(id, name, expectedName);
  }

  function deleteHome(id) {
    // Phase 5D read-only cloud mode.
    if (hasCloudSnapshot()) return false;
    return localDeleteHome(id);
  }

  function localDeleteHome(id) {
    if (!currentCustomerId) return false;

    // Deleting a home only ungroups its projects -- the projects themselves
    // (and their mood boards / space plans) are never touched.
    var homes = localHomes().filter(function (h) {
      return h.id !== id;
    });

    return saveHomes(homes);
  }

  // ---- Legacy (pre-4D.3 global) data: detection + explicit, confirmed import ----
  //
  // These are the ONLY functions in this file that ever touch the old
  // unscoped modernStudioProjects / modernStudioHomes keys. They never
  // populate getProjects()/getHomes()/getProjectsForRoom() -- Mood Board and
  // Space Planner's "Add to Project" dropdowns can therefore never surface
  // another customer's (or nobody's) old unscoped data. Legacy data only
  // ever becomes visible through the explicit import flow below, and only
  // after the signed-in customer confirms it.

  function promptSeenKey() {
    return currentCustomerId
      ? ('modernStudioLegacyPromptSeen:' + currentCustomerId)
      : null;
  }

  function hasSeenLegacyPrompt() {
    var key = promptSeenKey();

    if (!key) return true; // no customer scoped yet -- nothing to prompt

    try {
      return localStorage.getItem(key) === '1';
    } catch (e) {
      return true;
    }
  }

  function markLegacyPromptSeen() {
    var key = promptSeenKey();

    if (!key) return;

    try {
      localStorage.setItem(key, '1');
    } catch (e) {}
  }

  function dismissLegacyPrompt() {
    markLegacyPromptSeen();
  }

  function hasLegacyData() {
    var legacyProjects = readKey(LEGACY_PROJECTS_KEY);
    var legacyHomes = readKey(LEGACY_HOMES_KEY);

    return legacyProjects.length > 0 || legacyHomes.length > 0;
  }

  // Copies (never moves/deletes) legacy Projects/Homes into the current
  // customer's namespaced keys. Conservative collision handling: if a
  // record with the same ID already exists in this customer's own scoped
  // data, the existing scoped record is left untouched and the legacy copy
  // is skipped (not overwritten). A Home's projectIds is filtered down to
  // only the project IDs that are actually present after import (whether
  // pre-existing or newly copied), so a Home never ends up referencing a
  // project that was skipped -- preserving referential consistency without
  // a full merge engine.
  //
  // Phase 4D.4: returns { importedProjects, importedHomes, success }.
  // success is false if EITHER write fails -- if the projects write fails,
  // nothing is persisted and nothing is marked imported. If projects saved
  // but homes did not, that is also reported as a failure (conservative:
  // never claim complete success on a partial write), the prompt is NOT
  // marked seen either way on failure, and the legacy globals are never
  // touched regardless of outcome, so a retry is always possible.

  function importLegacyData() {
    if (!currentCustomerId) {
      return {
        importedProjects: 0,
        importedHomes: 0,
        success: false
      };
    }

    // Phase 5D cloud snapshot is intentionally read-only. Do not merge
    // browser legacy data into a cloud-backed view.
    if (hasCloudSnapshot()) {
      return {
        importedProjects: 0,
        importedHomes: 0,
        success: false
      };
    }

    var legacyProjects = readKey(LEGACY_PROJECTS_KEY);
    var legacyHomes = readKey(LEGACY_HOMES_KEY);

    var scopedProjects = localProjects();
    var existingProjectIds = {};

    scopedProjects.forEach(function (p) {
      existingProjectIds[p.id] = true;
    });

    var presentAfterImport = {};

    Object.keys(existingProjectIds).forEach(function (id) {
      presentAfterImport[id] = true;
    });

    var mergedProjects = scopedProjects.slice();
    var importedProjects = 0;

    legacyProjects.forEach(function (p) {
      if (existingProjectIds[p.id]) {
        // Already present in this customer's own scope -- keep the
        // existing record, but it still counts as "present" for the
        // purpose of keeping Home references valid below.
        presentAfterImport[p.id] = true;
        return;
      }

      mergedProjects.push(p);
      presentAfterImport[p.id] = true;
      importedProjects += 1;
    });

    if (!saveProjects(mergedProjects)) {
      return {
        importedProjects: 0,
        importedHomes: 0,
        success: false
      };
    }

    var scopedHomes = localHomes();
    var existingHomeIds = {};

    scopedHomes.forEach(function (h) {
      existingHomeIds[h.id] = true;
    });

    var mergedHomes = scopedHomes.slice();
    var importedHomes = 0;

    legacyHomes.forEach(function (h) {
      if (existingHomeIds[h.id]) return;

      var filteredProjectIds = (h.projectIds || []).filter(function (pid) {
        return presentAfterImport[pid];
      });

      mergedHomes.push({
        id: h.id,
        name: h.name,
        projectIds: filteredProjectIds,
        createdAt: h.createdAt,
        updatedAt: h.updatedAt
      });

      importedHomes += 1;
    });

    if (!saveHomes(mergedHomes)) {
      // Projects were saved but homes weren't -- conservative: do not
      // report success, and do not mark the prompt seen, so the customer
      // can try again. The projects half that did persist will simply be
      // picked up again (as already-present, so not re-duplicated) on a
      // retry, since existingProjectIds is recomputed fresh next time.
      return {
        importedProjects: importedProjects,
        importedHomes: 0,
        success: false
      };
    }

    markLegacyPromptSeen();

    return {
      importedProjects: importedProjects,
      importedHomes: importedHomes,
      success: true
    };
  }

  // ---- Phase 6B: local -> cloud migration support (read-only) ----
  //
  // readLocalForMigration() reads the browser copies directly, even while
  // the cloud snapshot is showing. It returns deep copies and never writes.
  // The browser copies of Projects and Homes are never changed by the
  // migration; the ONLY key it writes is the status-only marker below.

  function copyList(list) {
    try {
      return Array.isArray(list) ? JSON.parse(JSON.stringify(list)) : [];
    } catch (e) {
      return [];
    }
  }

  function readLocalForMigration() {
    return {
      scoped: {
        projects: copyList(readKey(projectsKey())),
        homes: copyList(readKey(homesKey()))
      },
      unscoped: {
        projects: copyList(readKey(LEGACY_PROJECTS_KEY)),
        homes: copyList(readKey(LEGACY_HOMES_KEY))
      }
    };
  }

  function migrationMarkerKey() {
    return currentCustomerId
      ? ('modernStudioCloudMigration:' + currentCustomerId)
      : null;
  }

  // Marker holds ids and statuses only (D4): resolvedIds, deferredUntil,
  // unscopedDecision. No Project or Home content is ever stored in it.
  function getMigrationMarker() {
    var key = migrationMarkerKey();
    var empty = { resolvedIds: {} };

    if (!key) return empty;

    try {
      var raw = localStorage.getItem(key);
      var m = raw ? JSON.parse(raw) : null;

      if (!m || typeof m !== 'object' || Array.isArray(m)) return empty;

      var out = { resolvedIds: {} };

      if (m.resolvedIds && typeof m.resolvedIds === 'object') {
        Object.keys(m.resolvedIds).forEach(function (id) {
          if (typeof m.resolvedIds[id] === 'string') out.resolvedIds[id] = m.resolvedIds[id];
        });
      }
      if (typeof m.deferredUntil === 'number') out.deferredUntil = m.deferredUntil;
      if (m.unscopedDecision === 'include' || m.unscopedDecision === 'exclude') {
        out.unscopedDecision = m.unscopedDecision;
      }

      return out;
    } catch (e) {
      return empty;
    }
  }

  function setMigrationMarker(m) {
    var key = migrationMarkerKey();

    if (!key || !m || typeof m !== 'object') return false;

    var clean = { resolvedIds: {} };

    Object.keys(m.resolvedIds || {}).forEach(function (id) {
      if (typeof m.resolvedIds[id] === 'string') clean.resolvedIds[id] = m.resolvedIds[id];
    });
    if (typeof m.deferredUntil === 'number') clean.deferredUntil = m.deferredUntil;
    if (m.unscopedDecision === 'include' || m.unscopedDecision === 'exclude') {
      clean.unscopedDecision = m.unscopedDecision;
    }

    try {
      localStorage.setItem(key, JSON.stringify(clean));
      return true;
    } catch (e) {
      return false;
    }
  }

  // D10: how many of this customer's browser-only Projects are not yet in
  // the cloud view. Only meaningful while the cloud snapshot is showing;
  // returns 0 otherwise. Unscoped (pre-sign-in) Projects count only after
  // the customer said they are theirs.
  function countLocalOnlyProjects() {
    if (!hasCloudSnapshot()) return 0;

    var cloudIds = {};
    cloudProjectsSnapshot.forEach(function (p) {
      if (p && p.clientLegacyId) cloudIds[p.clientLegacyId] = true;
    });

    var m = getMigrationMarker();
    var resolved = m.resolvedIds || {};
    var seen = {};
    var local = readLocalForMigration();
    var list = local.scoped.projects;

    if (m.unscopedDecision === 'include') list = list.concat(local.unscoped.projects);

    var n = 0;
    list.forEach(function (p) {
      if (!p || typeof p !== 'object' || typeof p.id !== 'string') return;
      var id = p.id.trim();
      if (!id || seen[id] || cloudIds[id] || resolved[id]) return;
      seen[id] = true;
      n += 1;
    });

    return n;
  }

  // ---- Phase 6B #3: account-saving status and browser-only work ----

  // Combine the two list replies' status. Only BOTH saying true is 'on';
  // both false is 'off'; mixed, missing or non-boolean is 'unknown'
  // (treated as off: new work is saved on this device, nothing is sent).
  function combineWrites(projectsResult, homesResult) {
    var a = projectsResult && projectsResult.writesEnabled;
    var b = homesResult && homesResult.writesEnabled;
    if (a === true && b === true) return 'on';
    if (a === false && b === false) return 'off';
    return 'unknown';
  }

  // How many browser-only records would remain beside the given account lists.
  function countDeviceOnly(cloudProjects, cloudHomes) {
    var hidden = {};
    var resolved = getMigrationMarker().resolvedIds || {};
    Object.keys(resolved).forEach(function (id) {
      if (resolved[id] === 'moved' || resolved[id] === 'deleted_in_account') hidden[id] = true;
    });
    (cloudProjects || []).concat(cloudHomes || []).forEach(function (r) {
      if (r && r.clientLegacyId) hidden[r.clientLegacyId] = true;
    });
    return localProjects().concat(localHomes()).filter(function (r) {
      return r && typeof r.id === 'string' && !hidden[r.id];
    }).length;
  }

  // The one view rule shared by My Projects, Mood Board and Space Planner.
  //   - Account can't be read -> this device (status unknown).
  //   - Account has records -> account view (read-only unless writes 'on').
  //   - Account empty -> account view only when writes are 'on' and there is
  //     no browser-only work; otherwise this device.
  function decideCloudView(projectsResult, homesResult) {
    var ok = !!projectsResult && projectsResult.ok === true && Array.isArray(projectsResult.data) &&
      !!homesResult && homesResult.ok === true && Array.isArray(homesResult.data);
    if (!ok) return { activate: false, writes: 'unknown', readFailed: true };
    var writes = combineWrites(projectsResult, homesResult);
    var cloudHas = projectsResult.data.length > 0 || homesResult.data.length > 0;
    if (cloudHas) return { activate: true, writes: writes, readFailed: false };
    var deviceOnly = countDeviceOnly(projectsResult.data, homesResult.data);
    return { activate: writes === 'on' && deviceOnly === 0, writes: writes, readFailed: false };
  }

  function localHomeForProject(projectId) {
    return visibleLocalHomes().filter(function (h) {
      return h.projectIds.indexOf(projectId) !== -1;
    })[0] || null;
  }

  function localProjectById(id) {
    if (!id) return null;
    return visibleLocalProjects().filter(function (p) { return p.id === id; })[0] || null;
  }

  // Tools: device Projects for a room. Browser Projects store the tool's
  // room key (e.g. 'bedroom-collection'); older ones may hold the canonical
  // key, so both are accepted.
  function localProjectsForRoom(roomKeys) {
    var keys = [].concat(roomKeys || []);
    return visibleLocalProjects().filter(function (p) { return keys.indexOf(p.room) !== -1; });
  }

  // Tools: which record a ?project= link opens, and from where. A device
  // link (saved=device) only ever reads THIS customer's browser copies; a
  // copy that has since moved opens its account record instead.
  function resolveOpenedProject(pid, savedParam) {
    if (!pid) return null;
    if (savedParam === 'device') {
      var local = localProjectById(pid);
      if (local) return { project: local, source: 'device' };
    }
    var found = getProject(pid);
    if (!found) return null;
    return { project: found, source: hasCloudSnapshot() ? 'account' : 'device' };
  }

  // Tools: the device copy made from an account Project while account
  // saving is off (one copy per account Project, updated on later saves).
  function deviceCopyOf(accountProjectId) {
    if (!accountProjectId) return null;
    return visibleLocalProjects().filter(function (p) { return p.copyOf === accountProjectId; })[0] || null;
  }

  // Tools: save the current Mood Board / Space Plan on this device.
  // input = { field: 'moodBoard'|'spacePlan', value, projectId (device id or
  // ''), copyFrom (account Project or null), name, room, roomLabel }.
  // Returns { project, kind } or null. Never touches the account.
  function saveToolItemToDevice(input) {
    if (!currentCustomerId || !input || (input.field !== 'moodBoard' && input.field !== 'spacePlan')) return null;
    var attach = input.field === 'moodBoard' ? localAttachMoodBoard : localAttachSpacePlan;
    var saved;
    if (input.copyFrom) {
      var src = input.copyFrom;
      var existing = deviceCopyOf(src.id);
      if (existing) {
        saved = attach(existing.id, input.value);
        return saved ? { project: saved, kind: 'copy_updated' } : null;
      }
      var copy = localCreateProject((src.name || 'Project') + ' (copy)', input.room, input.roomLabel || src.roomLabel || null, {
        copyOf: src.id,
        moodBoard: src.moodBoard || null,
        spacePlan: src.spacePlan || null
      });
      if (!copy) return null;
      saved = attach(copy.id, input.value);
      return saved ? { project: saved, kind: 'copy_created' } : null;
    }
    if (input.projectId) {
      saved = attach(input.projectId, input.value);
      return saved ? { project: saved, kind: 'updated' } : null;
    }
    var created = localCreateProject(input.name, input.room, input.roomLabel);
    if (!created) return null;
    saved = attach(created.id, input.value);
    return saved ? { project: saved, kind: 'created' } : null;
  }

  // Tools: the helper line under "Add to a Project" for each save mode.
  function toolSaveNote(item, mode, name) {
    var q = '“' + (name || '') + '”';
    if (mode === 'account') {
      return 'This ' + item + ' is saved to your Môdern Studio™ account when you select “Add to Project.” Changes you make afterward aren’t saved until you add it again.';
    }
    if (mode === 'copy') {
      return q + ' is in your account. Saving to your account is paused, so your changes can be saved as a copy on this device instead. The Project in your account won’t change.';
    }
    if (mode === 'opened_device') {
      return q + ' is saved on this device. Adding here updates it on this device; you can move it to your account from My Projects.';
    }
    if (hasCloudSnapshot()) {
      return 'Adding to Projects in your account is paused for now. This ' + item + ' will be saved to a Project on this device, and you can move it to your account later.';
    }
    return 'This ' + item + ' is saved to a Project in this browser when you select “Add to Project.”';
  }

  // Tools (Mood Board, Space Planner): the "Add to a Project" block whenever
  // the save goes to THIS DEVICE. The account path stays in each tool (the
  // shared conflict-aware saver in studio-cloud-projects.js).
  //
  // Modes:
  //   'account'       -- account view with account saving on (tool's own saver)
  //   'opened_device' -- a browser Project was opened (saved=device link, or
  //                      this device's own view): saves update it here
  //   'copy'          -- an account Project was opened while account saving
  //                      is off/unknown: saves create/update a device COPY;
  //                      the account Project is never changed
  //   'device'        -- anything else: saves go to a Project on this device
  //
  // config = { item: 'board'|'plan', field: 'moodBoard'|'spacePlan',
  //   roomKeys(), room(), roomLabel(), buildValue(), defaultName(),
  //   select, nameInput, addBtn, note, storageNote, addLabel }
  function createDeviceToolSaver(config) {
    var opened = null;
    var preselectUsed = false;
    var addLabel = config.addLabel || (config.addBtn ? config.addBtn.textContent : 'Add to Project');

    function esc(v) {
      var d = document.createElement('div');
      d.textContent = v == null ? '' : String(v);
      return d.innerHTML;
    }

    function mode() {
      if (opened && opened.source === 'device') return 'opened_device';
      if (saveTarget() === 'account') return 'account';
      if (opened && opened.source === 'account') return 'copy';
      return 'device';
    }

    function populate() {
      var sel = config.select;
      if (!sel) return;
      var previous = sel.value;
      var list = localProjectsForRoom(config.roomKeys());
      sel.innerHTML = '<option value="">+ Start a new project</option>' + list.map(function (p) {
        return '<option value="' + esc(p.id) + '">' + esc(p.name) + '</option>';
      }).join('');
      var want = opened && opened.source === 'device' ? opened.project.id : previous;
      var found = !!want && list.some(function (p) { return p.id === want; });
      sel.value = found ? want : '';
      if (config.nameInput) config.nameInput.hidden = sel.value !== '';
    }

    function refreshUi() {
      var m = mode();
      var copyMode = m === 'copy';
      if (config.storageNote) {
        config.storageNote.textContent = toolSaveNote(config.item, m, opened && opened.project && opened.project.name);
      }
      if (config.select) config.select.hidden = copyMode;
      if (config.nameInput) config.nameInput.hidden = copyMode || (config.select && config.select.value !== '');
      if (config.addBtn) {
        config.addBtn.textContent = copyMode
          ? (deviceCopyOf(opened.project.id) ? 'Update the copy on this device' : 'Save a copy on this device')
          : addLabel;
        config.addBtn.setAttribute('data-save-mode', m);
      }
      if (m !== 'account' && !copyMode) populate();
    }

    function showNote(html) {
      if (!config.note) return;
      config.note.hidden = false;
      config.note.innerHTML = html;
    }

    function save() {
      var m = mode();
      var input = {
        field: config.field,
        value: config.buildValue(),
        room: config.room(),
        roomLabel: config.roomLabel()
      };
      if (m === 'copy') {
        input.copyFrom = opened.project;
      } else {
        input.projectId = config.select ? config.select.value : '';
        input.name = config.defaultName();
      }
      var result = saveToolItemToDevice(input);
      var link = ' <a href="/pages/my-projects">View My Projects →</a>';
      if (!result) {
        if (config.note) {
          config.note.hidden = false;
          config.note.textContent = "We couldn't save this Project. Please try again.";
        }
        return null;
      }
      var name = '“' + esc(result.project.name) + '”';
      if (result.kind === 'copy_created' || result.kind === 'copy_updated') {
        showNote((result.kind === 'copy_created' ? 'Saved a copy on this device: ' : 'Updated the copy on this device: ') + name +
          '. “' + esc(opened.project.name) + '” in your account wasn’t changed.' + link);
      } else if (hasCloudSnapshot()) {
        showNote('Saved to ' + name + ' on this device.' + link);
      } else {
        showNote('Added to ' + name + '.' + link);
      }
      if (config.nameInput && m !== 'copy') config.nameInput.value = '';
      refreshUi();
      if (config.select && m !== 'copy') {
        config.select.value = result.project.id;
        if (config.nameInput) config.nameInput.hidden = true;
      }
      return result;
    }

    return {
      mode: mode,
      setOpened: function (resolved) { opened = resolved && resolved.project ? resolved : null; preselectUsed = false; refreshUi(); },
      // Writes ON: an account Project opened from My Projects becomes the
      // "Add to Project" target once (the customer can still choose another).
      accountPreselect: function () {
        return opened && opened.source === 'account' && !preselectUsed ? opened.project.id : null;
      },
      markPreselectUsed: function () { preselectUsed = true; },
      // Room key the tool should select for the opened Project (its saved
      // board/plan room, else the Project's own room).
      openedRoom: function () { return opened ? opened.project.room : null; },
      opened: function () { return opened; },
      refreshUi: refreshUi,
      populate: populate,
      save: save
    };
  }

  var device = {
    getProjects: visibleLocalProjects,
    getHomes: visibleLocalHomes,
    getProject: localProjectById,
    getProjectsForRoom: localProjectsForRoom,
    getHomeForProject: localHomeForProject,
    createProject: function (name, room, roomLabel) { return localCreateProject(name, room, roomLabel); },
    attachMoodBoard: localAttachMoodBoard,
    attachSpacePlan: localAttachSpacePlan,
    deleteProject: localDeleteProject,
    createHome: localCreateHome,
    assignProjectToHome: localAssignProjectToHome,
    removeProjectFromHome: localRemoveProjectFromHome,
    deleteHome: localDeleteHome,
    renameHome: localRenameHome,
    copyOf: deviceCopyOf,
    saveToolItem: saveToolItemToDevice
  };

  window.MSGProjects = {
    setCustomer: setCustomer,

    // Phase 6B #3: account-saving status, view rule and browser-only work.
    setAccountWrites: setAccountWrites,
    getAccountWrites: getAccountWrites,
    saveTarget: saveTarget,
    combineWrites: combineWrites,
    decideCloudView: decideCloudView,
    resolveOpenedProject: resolveOpenedProject,
    toolSaveNote: toolSaveNote,
    createDeviceToolSaver: createDeviceToolSaver,
    device: device,

    // Phase 6B migration support (read-only local access + status marker).
    readLocalForMigration: readLocalForMigration,
    getMigrationMarker: getMigrationMarker,
    setMigrationMarker: setMigrationMarker,
    countLocalOnlyProjects: countLocalOnlyProjects,

    // Phase 5D read-only cloud snapshot surface.
    setCloudSnapshot: setCloudSnapshot,
    clearCloudSnapshot: clearCloudSnapshot,
    hasCloudSnapshot: hasCloudSnapshot,
    upsertCloudProject: upsertCloudProject,
    removeCloudProject: removeCloudProject,
    upsertCloudHome: upsertCloudHome,
    // Phase 6A-2: in-place, memory-only refresh after a version conflict.
    refreshCloudProject: refreshCloudProject,
    refreshCloudHome: refreshCloudHome,

    getProjects: getProjects,
    getProjectsForRoom: getProjectsForRoom,
    getProject: getProject,
    createProject: createProject,
    attachMoodBoard: attachMoodBoard,
    attachSpacePlan: attachSpacePlan,
    deleteProject: deleteProject,

    getHomes: getHomes,
    createHome: createHome,
    assignProjectToHome: assignProjectToHome,
    removeProjectFromHome: removeProjectFromHome,
    getHomeForProject: getHomeForProject,
    deleteHome: deleteHome,
    renameHome: renameHome,

    hasLegacyData: hasLegacyData,
    hasSeenLegacyPrompt: hasSeenLegacyPrompt,
    dismissLegacyPrompt: dismissLegacyPrompt,
    importLegacyData: importLegacyData
  };
})();
